import { Alert, Button, Form, Input } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../app/auth.tsx";
import { lockedMessage, rateLimitedMessage } from "../app/retry-after.ts";
import { AuthCard } from "../components/AuthCard.tsx";

interface LoginForm {
  userName: string;
  password: string;
}

/** Router state set by the reset-password screen after a successful reset. */
export interface LoginLocationState {
  passwordReset?: boolean;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordReset = (location.state as LoginLocationState | null)?.passwordReset === true;
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function onFinish(values: LoginForm) {
    setSubmitting(true);
    setError(null);
    const result = await login(values.userName.trim(), values.password);
    setSubmitting(false);
    if (result.ok) {
      navigate("/", { replace: true });
      return;
    }
    if (result.status === 423) setError(lockedMessage(t, result.retryAfterSeconds));
    else if (result.status === 429) setError(rateLimitedMessage(t, result.retryAfterSeconds));
    else if (result.status === 401) setError(t("auth.invalid"));
    else setError(t("app.unavailable"));
  }

  return (
    <AuthCard subtitle={t("auth.title")}>
      {passwordReset && !error && <Alert type="success" showIcon title={t("auth.reset.done")} style={{ marginBottom: 16 }} role="status" />}
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} role="alert" />}
      <Form<LoginForm> layout="vertical" onFinish={onFinish} requiredMark={false} autoComplete="on">
        <Form.Item name="userName" label={t("auth.userName")} rules={[{ required: true, message: t("auth.required") }]}>
          <Input autoComplete="username" autoFocus size="large" />
        </Form.Item>
        <Form.Item name="password" label={t("auth.password")} rules={[{ required: true, message: t("auth.required") }]}>
          <Input.Password autoComplete="current-password" size="large" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
          {t("auth.login")}
        </Button>
      </Form>
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Link to="/forgot-password">{t("auth.forgotLink")}</Link>
      </div>
    </AuthCard>
  );
}
